import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { userApi } from '../../services/api'

export interface User {
  id: string
  uuid?: string
  email: string
  name: string
  firstName?: string
  lastName?: string
  phoneNumber?: string
  status: 'active' | 'inactive' | 'suspended'
  emailVerified: boolean
  lastLoginAt: Date | null
  registeredAt: Date
  updatedAt: Date
  profilePicture?: string
  country?: string
  city?: string
  timezone?: string
  preferredLanguage?: string
  loginCount?: number
  deviceInfo?: {
    platform?: string
    deviceType?: string
    lastUsedVersion?: string
  }
}

export interface UserListItem {
  id: string
  uuid: string
  name: string
  email: string
  status: 'active' | 'inactive' | 'suspended'
  emailVerified: boolean
  lastLoginAt: string | null
  registeredAt: string
  updatedAt: string
  loginCount: number
}

export interface UserActivityLog {
  id: string
  userId: string
  action: string
  details: any
  ipAddress: string
  userAgent: string
  timestamp: string
}

export interface UserDevice {
  id: string
  deviceId: string
  platform: string
  deviceType: string
  deviceName?: string
  lastUsedVersion: string
  lastActiveAt: string
  firstSeenAt: string
  loginCount: number
  ipAddress?: string
  userAgent?: string
  pushToken?: string
}

export interface Organization {
  id: string
  uuid: string
  name: string
  description?: string
  email?: string
  phone?: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface UserOrganization {
  organization: Organization
  role: {
    id: string
    name: string
    description?: string
  }
  branch?: {
    id: string
    name: string
  }
  isOwner: boolean
  joinedAt: string
  externalId?: string
  provider?: 'azure-ad' | 'google' | 'okta' | 'generic-saml' | 'generic-oidc' | 'internal'
}

interface UsersState {
  users: UserListItem[]
  selectedUser: User | null
  activityLogs: UserActivityLog[]
  devices: UserDevice[]
  userOrganizations: UserOrganization[]
  loading: boolean
  error: string | null
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
  filters: {
    search: string
    status: 'active' | 'inactive' | 'suspended' | 'all'
    emailVerified: boolean | 'all'
    sortBy: string
    sortOrder: 'asc' | 'desc'
    dateFrom?: string
    dateTo?: string
  }
  stats: {
    totalUsers: number
    activeUsers: number
    inactiveUsers: number
    suspendedUsers: number
    newUsersThisMonth: number
  }
}

const initialState: UsersState = {
  users: [],
  selectedUser: null,
  activityLogs: [],
  devices: [],
  userOrganizations: [],
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },
  filters: {
    search: '',
    status: 'all',
    emailVerified: 'all',
    sortBy: 'createdAt',
    sortOrder: 'desc',
  },
  stats: {
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    suspendedUsers: 0,
    newUsersThisMonth: 0,
  },
}

// Async thunks for API operations
export const fetchUsers = createAsyncThunk(
  'users/fetchAll',
  async (params?: {
    page?: number
    limit?: number
    search?: string
    status?: 'active' | 'inactive' | 'suspended' | 'all'
    emailVerified?: boolean | 'all'
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
    dateFrom?: string
    dateTo?: string
  }) => {
    try {
      const response = await userApi.listUsers(params)
      return response.data
    } catch (error: any) {
      // Return mock data for demonstration if API not available
      if (error.response?.status === 404) {
        return {
          data: [
            {
              id: '1',
              uuid: 'user-1',
              name: 'John Doe',
              email: 'john.doe@example.com',
              status: 'active',
              emailVerified: true,
              lastLoginAt: new Date().toISOString(),
              registeredAt: '2024-01-15T10:00:00Z',
              updatedAt: new Date().toISOString(),
              loginCount: 45,
            },
            {
              id: '2',
              uuid: 'user-2',
              name: 'Jane Smith',
              email: 'jane.smith@example.com',
              status: 'active',
              emailVerified: true,
              lastLoginAt: new Date(Date.now() - 86400000).toISOString(),
              registeredAt: '2024-02-20T14:30:00Z',
              updatedAt: new Date().toISOString(),
              loginCount: 23,
            },
            {
              id: '3',
              uuid: 'user-3',
              name: 'Bob Wilson',
              email: 'bob.wilson@example.com',
              status: 'inactive',
              emailVerified: false,
              lastLoginAt: null,
              registeredAt: '2024-03-10T09:15:00Z',
              updatedAt: new Date().toISOString(),
              loginCount: 0,
            },
            {
              id: '4',
              uuid: 'user-4',
              name: 'Alice Brown',
              email: 'alice.brown@example.com',
              status: 'suspended',
              emailVerified: true,
              lastLoginAt: '2024-03-01T16:45:00Z',
              registeredAt: '2023-12-05T11:20:00Z',
              updatedAt: new Date().toISOString(),
              loginCount: 67,
            },
            {
              id: '5',
              uuid: 'user-5',
              name: 'Charlie Davis',
              email: 'charlie.davis@example.com',
              status: 'active',
              emailVerified: true,
              lastLoginAt: new Date(Date.now() - 3600000).toISOString(),
              registeredAt: '2024-01-25T13:00:00Z',
              updatedAt: new Date().toISOString(),
              loginCount: 89,
            },
          ],
          pagination: {
            page: params?.page || 1,
            limit: params?.limit || 20,
            total: 5,
            totalPages: 1,
          },
        }
      }
      throw error
    }
  }
)

export const fetchUserById = createAsyncThunk(
  'users/fetchById',
  async (userId: string) => {
    const response = await userApi.getUserById(userId)
    return response.data
  }
)

export const searchUsers = createAsyncThunk(
  'users/search',
  async (query: string) => {
    const response = await userApi.searchUsers(query)
    return response.data
  }
)

export const suspendUser = createAsyncThunk(
  'users/suspend',
  async ({ userId, reason }: { userId: string; reason?: string }) => {
    const response = await userApi.suspendUser(userId, { reason })
    return response.data
  }
)

export const unsuspendUser = createAsyncThunk(
  'users/unsuspend',
  async (userId: string) => {
    const response = await userApi.unsuspendUser(userId)
    return response.data
  }
)

export const resetUserPassword = createAsyncThunk(
  'users/resetPassword',
  async ({ userId, sendEmail }: { userId: string; sendEmail: boolean }) => {
    const response = await userApi.resetUserPassword(userId, { sendEmail })
    return response.data
  }
)

export const fetchUserActivityLogs = createAsyncThunk(
  'users/fetchActivityLogs',
  async (userId: string) => {
    const response = await userApi.getUserActivityLogs(userId)
    return response.data
  }
)

export const fetchUserStats = createAsyncThunk(
  'users/fetchStats',
  async () => {
    try {
      const response = await userApi.getUserStats()
      return response.data
    } catch (error: any) {
      // Return mock stats if API not available
      if (error.response?.status === 404) {
        return {
          totalUsers: 1234,
          activeUsers: 987,
          inactiveUsers: 147,
          suspendedUsers: 100,
          newUsersThisMonth: 56,
        }
      }
      throw error
    }
  }
)

export const fetchUserDevices = createAsyncThunk(
  'users/fetchDevices',
  async (userId: string) => {
    const response = await userApi.getUserDevices(userId)
    return response.data
  }
)

export const fetchUserOrganizations = createAsyncThunk(
  'users/fetchOrganizations',
  async (userId: string) => {
    // Fetch organizations and ownerships in parallel
    const [orgsResponse, ownershipsResponse] = await Promise.all([
      userApi.getUserOrganizations(userId),
      userApi.getUserOrganizationOwnerships(userId)
    ])
    
    const organizations = orgsResponse.data
    const ownerships = ownershipsResponse.data
    
    // Mark organizations where user is owner
    return organizations.map((org: any) => ({
      ...org,
      isOwner: ownerships.some((ownership: any) => 
        ownership.organizationId === org.organization.id
      )
    }))
  }
)

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<UsersState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearFilters: (state) => {
      state.filters = initialState.filters
    },
    clearError: (state) => {
      state.error = null
    },
    setSelectedUser: (state, action: PayloadAction<User | null>) => {
      state.selectedUser = action.payload
    },
    updateUserInList: (state, action: PayloadAction<UserListItem>) => {
      const index = state.users.findIndex(user => user.id === action.payload.id)
      if (index !== -1) {
        state.users[index] = action.payload
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch users
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false
        state.users = action.payload.data || []
        state.pagination = action.payload.pagination || {
          page: 1,
          limit: 20,
          total: 0,
          totalPages: 0,
        }
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch users'
      })

    // Fetch user by ID
    builder
      .addCase(fetchUserById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.loading = false
        state.selectedUser = action.payload
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch user'
      })

    // Search users
    builder
      .addCase(searchUsers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(searchUsers.fulfilled, (state, action) => {
        state.loading = false
        state.users = action.payload.data || []
        state.pagination = action.payload.pagination || state.pagination
      })
      .addCase(searchUsers.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to search users'
      })

    // Suspend user
    builder
      .addCase(suspendUser.fulfilled, (state, action) => {
        // Update user in list
        const index = state.users.findIndex(user => user.id === action.payload.id)
        if (index !== -1) {
          state.users[index] = { ...state.users[index], status: 'suspended' }
        }
        // Update selected user if it's the same
        if (state.selectedUser?.id === action.payload.id) {
          state.selectedUser.status = 'suspended'
        }
      })
      .addCase(suspendUser.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to suspend user'
      })

    // Unsuspend user
    builder
      .addCase(unsuspendUser.fulfilled, (state, action) => {
        // Update user in list
        const index = state.users.findIndex(user => user.id === action.payload.id)
        if (index !== -1) {
          state.users[index] = { ...state.users[index], status: 'active' }
        }
        // Update selected user if it's the same
        if (state.selectedUser?.id === action.payload.id) {
          state.selectedUser.status = 'active'
        }
      })
      .addCase(unsuspendUser.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to unsuspend user'
      })

    // Reset password
    builder
      .addCase(resetUserPassword.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to reset password'
      })

    // Fetch activity logs
    builder
      .addCase(fetchUserActivityLogs.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchUserActivityLogs.fulfilled, (state, action) => {
        state.loading = false
        state.activityLogs = action.payload
      })
      .addCase(fetchUserActivityLogs.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch activity logs'
      })

    // Fetch stats
    builder
      .addCase(fetchUserStats.fulfilled, (state, action) => {
        state.stats = action.payload
      })
      .addCase(fetchUserStats.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch user stats'
      })

    // Fetch devices
    builder
      .addCase(fetchUserDevices.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchUserDevices.fulfilled, (state, action) => {
        state.loading = false
        state.devices = action.payload
      })
      .addCase(fetchUserDevices.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch user devices'
      })

    // Fetch organizations
    builder
      .addCase(fetchUserOrganizations.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchUserOrganizations.fulfilled, (state, action) => {
        state.loading = false
        state.userOrganizations = action.payload
      })
      .addCase(fetchUserOrganizations.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch user organizations'
      })
  },
})

export const {
  setFilters,
  clearFilters,
  clearError,
  setSelectedUser,
  updateUserInList,
} = usersSlice.actions

export default usersSlice.reducer